import { useTable } from 'react-table'
import { Await, Link } from '@remix-run/react'
import clsx from 'clsx'
import {
	ArrowDownIcon,
	ArrowUpIcon,
	ArrowRightIcon,
	BidIcon,
	OpenInNewIcon,
	WebsiteIcon,
} from '../icons'
import { Suspense, useMemo, useRef } from 'react'
import TypographyLabel from '../typography/TypographyLabel'
import {
	asyncTruncateStringInMiddle,
	getMetadataProfileURL,
} from '#app/utils/misc.tsx'
import InternalLink from './internalLink'

const ExhibitionRow = ({ value, column, row }) => {
	return (
		<div className="flex items-center gap-x-4 p-2">
			<BidIcon className="h-3.5 w-3.5 text-textSecondaryLight dark:text-textSecondaryDark" />
			<div className="truncate break-words">
				<TypographyLabel size="medium" isBold={true}>
					{row.original.properties.name}
				</TypographyLabel>
			</div>
		</div>
	)
}

const LocationRow = ({ value, column, row }) => {
	return (
		<div className="flex items-center">
			<TypographyLabel size="medium" additionalClasses="truncate break-words">
				{row.original.properties.location}
			</TypographyLabel>
		</div>
	)
}

const LinksRow = ({ value, column, row }) => {
	return (
		<div className="flex items-center justify-center gap-x-4 py-0 pl-0 pr-2">
			<a href={row.original.properties.url} target="_blank">
				<WebsiteIcon className="h-4 w-4" aria-hidden="true" />
			</a>
		</div>
	)
}

const SignatureRow = ({ value, column, row }) => {
	return (
		<div className="flex items-center justify-center gap-x-4 py-0 pl-0 pr-2">
			<Link to={row.original.signature} target="_blank">
				<OpenInNewIcon className="h-4 w-4" aria-hidden="true" />
			</Link>
		</div>
	)
}

const ProvenanceRow = ({ row }) => {
	const metadataProfileURL = getMetadataProfileURL(row.original)
	return (
		<InternalLink
			to={metadataProfileURL || ''}
			prefetch="none"
			additionalClasses="flex items-center justify-center py-4"
		>
			<ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
		</InternalLink>
	)
}

export default function DataTable({
	data,
	showFilter = true,
	showButton = true,
	sortRow = 'date',
	showStats = false,
	walletId = null,
	entryTitle = 'NFT',
}) {
	const tableContainerRef = useRef(null)

	const columns = [
		{
			Header: 'Exhibition',
			id: 'name',
			Cell: ExhibitionRow,
		},
		{
			Header: 'Location',
			id: 'location',
			Cell: LocationRow,
		},
		{
			Header: 'Start-end Date',
			id: 'date',
			accessor: row => (
				<TypographyLabel size="medium">
					{new Date(row.properties.startDate).toLocaleDateString() +
						' - ' +
						new Date(row.properties.endDate).toLocaleDateString()}
				</TypographyLabel>
			),
		},
		{
			Header: 'Exhibitor',
			id: 'exhibitor',
			accessor: row => (
				<Suspense fallback={'...'}>
					<Await
						resolve={asyncTruncateStringInMiddle(
							row.properties.receiverAddress,
						)}
					>
						{truncatedAddress => {
							return (
								<TypographyLabel size="medium">
									{truncatedAddress}
								</TypographyLabel>
							)
						}}
					</Await>
				</Suspense>
			),
		},
		{
			Header: 'Owner',
			id: 'owner',
			accessor: row => (
				<Suspense fallback={'...'}>
					<Await
						resolve={asyncTruncateStringInMiddle(row.properties.ownerAddress)}
					>
						{truncatedAddress => {
							return (
								<TypographyLabel size="medium">
									{truncatedAddress}
								</TypographyLabel>
							)
						}}
					</Await>
				</Suspense>
			),
		},
		{
			Header: 'Provenance',
			id: 'provenance',
			Cell: ProvenanceRow,
		},
	]

	// Get state and functions for building table
	const tableData = useMemo(() => data, [data])

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		footerGroups,
		rows,
		prepareRow,
	} = useTable({
		columns,
		data: tableData,
		autoResetHiddenColumns: false,
	})

	return (
		<div className="overflow-x-auto">
			<table {...getTableProps()} className="min-w-full table-auto">
				<thead className="py-6">
					{headerGroups.map((headerGroup, index) => (
						<tr
							key={index}
							className="border-b border-textInactiveLight font-abc text-paragraphSmall leading-6 text-textPrimaryLight dark:border-uiPrimaryDark dark:text-textPrimaryDark"
						>
							{headerGroup.headers.map((column, index) => {
								return (
									<th
										{...column.getHeaderProps()}
										key={index}
										scope="col"
										className={clsx(
											column.isSorted && 'underline',
											column.Header === 'Exhibition' &&
												'ml-2 inline min-w-[170px] max-w-[350px]',
											column.Header === 'Location' &&
												'min-w-[170px] max-w-[170px] pr-2 text-left md:table-cell',
											column.Header === 'Start-end Date' &&
												'min-w-[170px] text-left md:table-cell',
											column.Header === 'Exhibitor' &&
												'min-w-[170px] text-left md:table-cell',
											column.Header === 'Owner' &&
												'min-w-[170px] text-left md:table-cell',
											column.Header === 'Provenance' &&
												'min-w-[70px] md:table-cell',
											'relative h-10 py-0 pl-0 pr-0 align-text-bottom font-normal sm:pl-0',
										)}
									>
										{column.render('Header')}
										<span className="absolute top-[5px]">
											{column.isSorted ? (
												column.isSortedDesc ? (
													<ArrowDownIcon className="ml-[2px] h-3 w-3" />
												) : (
													<ArrowUpIcon className="ml-[2px] h-3 w-3" />
												)
											) : (
												''
											)}
										</span>
									</th>
								)
							})}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{rows.map((row, index) => {
						prepareRow(row)
						return (
							<tr
								key={index}
								className={clsx(
									'h-[72px] text-sm leading-6 text-textPrimaryLight dark:text-textPrimaryDark',
									row.index % 2 == 0 && 'bg-grey100 dark:bg-grey950',
								)}
							>
								{row.cells.map((cell, index) => {
									return (
										<td
											key={index}
											className={clsx(
												cell.column.Header === 'Exhibition' &&
													'max-w-[350px] pl-2 text-paragraphSmallBold md:table-cell',
												cell.column.Header === 'Location' &&
													'max-w-[170px] pr-2 md:table-cell',
												cell.column.Header === 'Start-end Date' &&
													'md:table-cell',
												cell.column.Header === 'Exhibitor' && 'md:table-cell',
												cell.column.Header === 'Owner' && 'md:table-cell',
												cell.column.Header === 'Provenance' && 'md:table-cell',
											)}
										>
											{cell.render('Cell')}
										</td>
									)
								})}
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}
